import { Fragment, type FormEvent, useEffect, useMemo, useState } from "react";
import {
  demoBranch,
  localizedDemoBranches,
  localizedHowJourneySteps,
  type DemoChoiceId,
} from "./experience.js";
import { siteCopy, type Locale } from "./content.js";
import { trackSectionView, trackWebsiteEvent } from "./analytics.js";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/u, "")
  ?? "https://api.actale.cc";

type FormStatus = "IDLE" | "SUBMITTING" | "SUCCESS" | "ERROR";
type FormError = "" | "INVALID_EMAIL" | "CONSENT" | "REQUEST";

const localeStorageKey = "actale_site_locale";

function initialLocale(): Locale {
  if (typeof window === "undefined") return "zh";
  const requested = new URLSearchParams(window.location.search).get("lang");
  if (requested === "en" || requested === "zh") return requested;
  try {
    const saved = window.localStorage.getItem(localeStorageKey);
    if (saved === "en" || saved === "zh") return saved;
  } catch {
    // The site still works when storage is unavailable.
  }
  return "zh";
}

function updateMeta(selector: string, content: string): void {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", content);
}

function updateLink(selector: string, href: string): void {
  document.querySelector<HTMLLinkElement>(selector)?.setAttribute("href", href);
}

function useSectionAnalytics() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const section = entry.target.getAttribute("data-analytics-section");
        if (!section) continue;
        trackSectionView(section);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.35 });

    const sections = document.querySelectorAll<HTMLElement>("[data-analytics-section]");
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}

function Brand({ locale, compact = false }: { locale: Locale; compact?: boolean }) {
  const t = siteCopy[locale];
  return (
    <a className={`brand${compact ? " brand--compact" : ""}`} href="#top" aria-label={t.brandHomeAria}>
      <span className="brand__mark"><img src="/branding/actale-mark-amber.svg" alt="" /></span>
      <span className="brand__type"><strong>同幕</strong><small>ACTALE</small></span>
    </a>
  );
}

function LanguageSwitch({ locale, onChange }: { locale: Locale; onChange: (locale: Locale) => void }) {
  const t = siteCopy[locale];
  return (
    <div className="language-switch" role="group" aria-label={t.languageAria}>
      <button type="button" aria-pressed={locale === "zh"} onClick={() => onChange("zh")}>中</button>
      <button type="button" aria-pressed={locale === "en"} onClick={() => onChange("en")}>EN</button>
    </div>
  );
}

function HeroStoryStage({ locale }: { locale: Locale }) {
  const t = siteCopy[locale];
  return (
    <figure className="hero-story" aria-label={t.hero.stageAria}>
      <picture className="hero-story__picture">
        <source media="(min-width: 1101px)" srcSet="/stories/echo-unanswered-hero-desktop-v1.jpg" />
        <source media="(min-width: 821px)" srcSet="/stories/echo-unanswered-hero-tablet-v1.jpg" />
        <img
          className="hero-story__image"
          src="/stories/echo-unanswered-hero-mobile-v1.jpg"
          alt={t.hero.imageAlt}
          width="1086"
          height="1448"
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      <div className="hero-story__shade" />
      <div className="story-interface" aria-label={t.phone.aria}>
        <i className="story-interface__side" aria-hidden="true" />
        <span className="story-interface__island" aria-hidden="true" />
        <div className="story-interface__screen">
          <div className="story-interface__top">
            <span>‹</span>
            <div><strong>{t.phone.title}</strong><small>{t.phone.subtitle}</small></div>
            <b>•••</b>
          </div>
          <p className="story-interface__role">{t.phone.role}</p>
          <div className="story-interface__event"><span>{t.phone.eventLabel}</span><strong>{t.phone.event}</strong></div>
          <div className="story-interface__voice">
            <span className="story-interface__play">▶</span>
            <div><small>{t.phone.voice}</small><p className="story-interface__wave"><i /><i /><i /><i /><i /><i /><b>0:08</b></p></div>
          </div>
          <blockquote>{t.phone.quote}</blockquote>
          <p className="story-interface__cue"><i />{t.phone.cue}</p>
          <div className="story-interface__composer"><span>{t.phone.composer}</span><b>↑</b></div>
        </div>
        <span className="story-interface__home" aria-hidden="true" />
      </div>
    </figure>
  );
}

function HowJourney({ locale }: { locale: Locale }) {
  const t = siteCopy[locale];
  const steps = localizedHowJourneySteps(locale);
  return (
    <ol className="how-storyboard" aria-label={t.how.aria}>
      {steps.map((step) => (
        <li className="how-storyboard__step" key={step.index}>
          <div
            className={`how-storyboard__art how-storyboard__art--${step.panel}`}
            role="img"
            aria-label={step.artAlt}
          >
            <img
              src="/site/actale-how-it-works-amber-v2.jpg"
              alt=""
              width="1254"
              height="1254"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="how-storyboard__copy">
            <span>{step.index}</span>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function DemoExperience({ locale }: { locale: Locale }) {
  const t = siteCopy[locale];
  const branches = localizedDemoBranches(locale);
  const [choice, setChoice] = useState<DemoChoiceId | null>(null);
  const branch = useMemo(() => choice ? demoBranch(choice, locale) : null, [choice, locale]);
  function choose(choiceId: DemoChoiceId) {
    setChoice(choiceId);
    trackWebsiteEvent({ name: "DEMO_CHOICE_SELECTED", section: "story", choice: choiceId });
  }
  return (
    <div className="demo-stage">
      <div className="demo-stage__visual">
        <img src="/stories/echo-unanswered-cover.png" alt={t.story.cardAlt} width="913" height="1723" loading="lazy" />
        <div className="demo-stage__story">
          <span>{t.story.cardLabel}</span>
          <h3>{t.story.cardTitle}</h3>
          <p>{t.story.cardSummary}</p>
          <div>{t.story.characters.map((character) => <b key={character}>{character}</b>)}</div>
        </div>
      </div>
      <div className="demo-stage__interaction" aria-live="polite">
        <div className="demo-label"><span>01:17</span><b>{t.story.demoLabel}</b><small>{t.story.demoNote}</small></div>
        <div className="demo-message"><strong>{t.story.caller}</strong><p>{t.story.callerLine}</p></div>
        <div className="demo-message"><strong>{t.story.responder}</strong><p>{t.story.responderLine}</p></div>
        {branch ? (
          <div className="demo-result" key={branch.id}>
            <div className="demo-player"><small>{t.story.playerChoice}</small><p>{branch.playerLine}</p></div>
            <div className="demo-message demo-message--response"><strong>{branch.responder}</strong><p>{branch.response}</p></div>
            <p className="demo-cue">{branch.cue}</p>
            <button className="text-button" type="button" onClick={() => setChoice(null)}>{t.story.retry}</button>
          </div>
        ) : (
          <div className="demo-choices">
            <p>{t.story.prompt}</p>
            {branches.map((item) => (
              <button key={item.id} type="button" onClick={() => choose(item.id)}>
                <span>{item.label}</span><ArrowIcon />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WaitlistForm({ locale }: { locale: Locale }) {
  const t = siteCopy[locale].waitlist.form;
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<FormStatus>("IDLE");
  const [error, setError] = useState<FormError>("");
  const [emailTouched, setEmailTouched] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim());
  const emailError = emailTouched && !emailValid
    ? (email.trim().length === 0 ? t.emailRequired : t.emailInvalid)
    : "";
  const formError = error === "INVALID_EMAIL"
    ? t.invalidSubmit
    : error === "CONSENT"
      ? t.consentRequired
      : error === "REQUEST"
        ? t.requestError
        : "";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEmailTouched(true);
    if (!emailValid) {
      setStatus("ERROR");
      setError("INVALID_EMAIL");
      return;
    }
    if (!consent) {
      setStatus("ERROR");
      setError("CONSENT");
      return;
    }
    setStatus("SUBMITTING");
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/waitlist`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, consent, company }),
      });
      if (!response.ok) throw new Error("WAITLIST_REQUEST_FAILED");
      trackWebsiteEvent({ name: "WAITLIST_SUBMIT_SUCCESS", section: "waitlist" });
      setStatus("SUCCESS");
    } catch {
      trackWebsiteEvent({ name: "WAITLIST_SUBMIT_ERROR", section: "waitlist" });
      setStatus("ERROR");
      setError("REQUEST");
    }
  }

  if (status === "SUCCESS") {
    return (
      <div className="waitlist-success" role="status">
        <span>✓</span><div><strong>{t.successTitle}</strong><p>{t.successBody}</p></div>
      </div>
    );
  }

  return (
    <form className="waitlist-form" onSubmit={submit} noValidate>
      <div className="honeypot" aria-hidden="true">
        <label htmlFor="company">{t.company}</label>
        <input id="company" name="company" value={company} onChange={(event) => setCompany(event.target.value)} tabIndex={-1} autoComplete="off" />
      </div>
      <label className="field-label" htmlFor="waitlist-email">{t.label}</label>
      <div className="waitlist-form__row">
        <input
          id="waitlist-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="name@example.com"
          value={email}
          onChange={(event) => { setEmail(event.target.value); if (status === "ERROR") { setStatus("IDLE"); setError(""); } }}
          onBlur={() => setEmailTouched(true)}
          required
          maxLength={320}
          aria-invalid={emailError ? "true" : "false"}
          aria-describedby={`${emailError ? "waitlist-email-error " : ""}${formError ? "waitlist-error" : "waitlist-note"}`.trim()}
        />
        <button type="submit" disabled={status === "SUBMITTING" || !emailValid || !consent}>
          {status === "SUBMITTING" ? t.submitting : t.submit}<ArrowIcon />
        </button>
      </div>
      {emailError ? <p className="field-error" id="waitlist-email-error">{emailError}</p> : null}
      <div className="consent-row">
        <label className="consent">
          <input type="checkbox" checked={consent} onChange={(event) => { setConsent(event.target.checked); if (error === "CONSENT") { setError(""); setStatus("IDLE"); } }} />
          <span>{t.consent}</span>
        </label>
        <a className="privacy-link" href={locale === "en" ? "/privacy-en.html" : "/privacy.html"}>{t.privacy}</a>
      </div>
      {formError ? <p className="form-error" id="waitlist-error" role="alert">{formError}</p> : <p className="form-note" id="waitlist-note">{t.note}</p>}
    </form>
  );
}

export function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const t = siteCopy[locale];
  useSectionAnalytics();

  useEffect(() => {
    document.documentElement.lang = t.meta.lang;
    document.title = t.meta.title;
    updateMeta('meta[name="description"]', t.meta.description);
    updateMeta('meta[property="og:locale"]', t.meta.ogLocale);
    updateMeta('meta[property="og:title"]', t.meta.ogTitle);
    updateMeta('meta[property="og:description"]', t.meta.ogDescription);
    updateMeta('meta[property="og:image:alt"]', t.meta.ogImageAlt);
    updateMeta('meta[name="twitter:title"]', t.meta.ogTitle);
    updateMeta('meta[name="twitter:description"]', t.meta.ogDescription);
    updateLink('link[rel="canonical"]', t.meta.canonical);
  }, [t]);

  function changeLocale(nextLocale: Locale): void {
    setLocale(nextLocale);
    try {
      window.localStorage.setItem(localeStorageKey, nextLocale);
    } catch {
      // Language switching does not depend on storage.
    }
    const url = new URL(window.location.href);
    if (nextLocale === "en") url.searchParams.set("lang", "en");
    else url.searchParams.delete("lang");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  return (
    <>
      <a className="skip-link" href="#main">{t.skip}</a>
      <header className="site-header" id="top">
        <div className="shell nav">
          <Brand locale={locale} />
          <nav aria-label={locale === "en" ? "Primary navigation" : "主要导航"}><a href="#how">{t.nav.how}</a><a href="#story">{t.nav.story}</a><a href="#why">{t.nav.why}</a></nav>
          <LanguageSwitch locale={locale} onChange={changeLocale} />
          <a className="nav-cta" href="#waitlist" onClick={() => trackWebsiteEvent({ name: "CTA_WAITLIST_CLICKED", section: "navigation" })}>{t.nav.join}</a>
        </div>
      </header>
      <main id="main">
        <section className="hero" data-analytics-section="hero">
          <div className="hero__texture" />
          <div className="shell hero__grid">
            <div className="hero__copy">
              <p className="eyebrow"><span /> {t.hero.eyebrow}</p>
              <h1>{t.hero.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h1>
              <p className="hero__lead">{t.hero.lead}</p>
              <div className="hero__actions"><a className="button button--signal" href="#waitlist" onClick={() => trackWebsiteEvent({ name: "CTA_WAITLIST_CLICKED", section: "hero" })}>{t.hero.join}<ArrowIcon /></a><a className="button button--ghost" href="#story" onClick={() => trackWebsiteEvent({ name: "CTA_DEMO_CLICKED", section: "hero" })}>{t.hero.demo}</a></div>
              <p className="hero__status"><i /> {t.hero.status} <span>·</span> iOS &amp; Android</p>
            </div>
          </div>
          <HeroStoryStage locale={locale} />
          <a className="scroll-cue" href="#how"><span>{t.hero.scroll}</span><i /></a>
        </section>

        <section className="world-strip" aria-label={t.worldStrip.aria}><div className="shell"><p>{t.worldStrip.intro}</p><div>{t.worldStrip.categories.map((category, index) => <Fragment key={category}><span>{category}</span>{index < t.worldStrip.categories.length - 1 ? <i /> : null}</Fragment>)}</div></div></section>

        <section className="section how" id="how" data-analytics-section="how">
          <div className="shell">
            <div className="section-heading"><p className="eyebrow eyebrow--dark"><span /> {t.how.eyebrow}</p><h2>{t.how.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2><p>{t.how.body}</p></div>
            <HowJourney locale={locale} />
          </div>
        </section>

        <section className="section story" id="story" data-analytics-section="story">
          <div className="shell">
            <div className="section-heading section-heading--row"><div><p className="eyebrow"><span /> {t.story.eyebrow}</p><h2>{t.story.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2></div><p>{t.story.body}</p></div>
            <DemoExperience locale={locale} />
          </div>
        </section>

        <section className="section why" id="why" data-analytics-section="why">
          <div className="shell">
            <div className="section-heading"><p className="eyebrow eyebrow--dark"><span /> {t.why.eyebrow}</p><h2>{t.why.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2></div>
            <div className="principles">{t.why.principles.map((principle) => <article key={principle.index}><span>{principle.index}</span><h3>{principle.title}</h3><p>{principle.body}</p></article>)}</div>
          </div>
        </section>

        <section className="worlds-showcase" aria-labelledby="worlds-title" data-analytics-section="worlds">
          <img src="/site/actale-worlds-v1.webp" alt={t.worlds.imageAlt} width="2048" height="1152" loading="lazy" />
          <div className="worlds-showcase__shade" />
          <div className="shell worlds-showcase__content">
            <p className="eyebrow"><span /> {t.worlds.eyebrow}</p>
            <h2 id="worlds-title">{t.worlds.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2>
            <p>{t.worlds.body}</p>
          </div>
        </section>

        <section className="section waitlist" id="waitlist" data-analytics-section="waitlist">
          <div className="waitlist__image"><img src="/stories/mist-over-lighthouse-cover.png" alt={t.waitlist.imageAlt} width="941" height="1672" loading="lazy" /></div>
          <div className="shell waitlist__content"><p className="eyebrow"><span /> {t.waitlist.eyebrow}</p><h2>{t.waitlist.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2><p>{t.waitlist.body}</p><WaitlistForm locale={locale} /></div>
        </section>

        <section className="section faq">
          <div className="shell faq__grid"><div><p className="eyebrow eyebrow--dark"><span /> {t.faq.eyebrow}</p><h2>{t.faq.title.map((line) => <span className="title-clause" key={line}>{line}</span>)}</h2></div><div className="faq-list">
            {t.faq.items.map((item) => <details key={item.question}><summary>{item.question}<span>＋</span></summary><p>{item.answer}</p></details>)}
          </div></div>
        </section>
      </main>
      <footer><div className="shell footer__grid"><Brand locale={locale} compact /><p>{t.footer.statement}</p><div className="footer-links"><a href="#waitlist" onClick={() => trackWebsiteEvent({ name: "CTA_WAITLIST_CLICKED", section: "footer" })}>{t.footer.waitlist}</a><a href={locale === "en" ? "/privacy-en.html" : "/privacy.html"}>{t.footer.privacy}</a><a href="mailto:hello@actale.cc">{t.footer.contact}</a><span>{t.footer.operator}</span><span>© 2026 ACTALE</span></div></div></footer>
    </>
  );
}
